import { useEffect, useMemo, useRef, useState } from "react";
import { getCategoriasPublic } from "../../services/public/CategoriasPublic";
import { getMarcasPublic } from "../../services/public/MarcasPublic";
import "./SmartFiltersPanel.css";
import { fileURL } from "../../utils/fileURL";
import { useStoreLoading } from "../../context/StoreLoadingContext";

const CAT_COLLAPSED = 24;
const BRAND_COLLAPSED = 24;

function SmartFiltersPanel({
  category,
  brand,
  onCategory,
  onBrand,
  onClear,
  mobileOpen = false,
  setMobileOpen = () => {},
}) {
  const { beginTask } = useStoreLoading();

  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);

  const [openCat, setOpenCat] = useState(true);
  const [openBrand, setOpenBrand] = useState(true);

  const [qCat, setQCat] = useState("");
  const [qBrand, setQBrand] = useState("");

  const [showAllCats, setShowAllCats] = useState(false);
  const [showAllBrands, setShowAllBrands] = useState(false);

  const catId = useMemo(() => (category ? Number(category) : null), [category]);
  const brandId = useMemo(() => (brand ? Number(brand) : null), [brand]);

  const hasActive = Boolean(catId || brandId);

  const scrollYRef = useRef(0);

  // ✅ Bloquear scroll SOLO si es móvil y el sheet está abierto
  useEffect(() => {
    if (!mobileOpen) return;
    if (window.innerWidth > 768) return;

    scrollYRef.current = window.scrollY || window.pageYOffset || 0;

    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.overflow = "";

      window.scrollTo(0, scrollYRef.current);
    };
  }, [mobileOpen]);

  // ✅ ESC solo aplica si está abierto
  useEffect(() => {
    if (!mobileOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen, setMobileOpen]);

  useEffect(() => {
    // Retiene la pantalla de carga hasta tener categorias y marcas.
    const finTarea = beginTask();

    (async () => {
      try {
        const [cats, brs] = await Promise.all([
          getCategoriasPublic(),
          getMarcasPublic(),
        ]);
        setCategorias(Array.isArray(cats) ? cats : []);
        setMarcas(Array.isArray(brs) ? brs : []);
      } catch (err) {
        console.error("Error loading filters:", err);
      } finally {
        finTarea();
      }
    })();
  }, [beginTask]);

  const selectedCategoryName = useMemo(() => {
    if (!catId) return null;
    return categorias.find((c) => Number(c.categoria_producto_id) === catId)?.nombre;
  }, [catId, categorias]);

  const selectedBrandName = useMemo(() => {
    if (!brandId) return null;
    return marcas.find((m) => Number(m.marca_producto_id) === brandId)?.nombre;
  }, [brandId, marcas]);

  const filteredCats = useMemo(() => {
    const t = qCat.trim().toLowerCase();
    const base = !t
      ? categorias
      : categorias.filter((c) => (c.nombre || "").toLowerCase().includes(t));
    return (showAllCats || t) ? base : base.slice(0, CAT_COLLAPSED);
  }, [qCat, categorias, showAllCats]);

  const filteredBrands = useMemo(() => {
    const t = qBrand.trim().toLowerCase();
    const base = !t
      ? marcas
      : marcas.filter((m) => (m.nombre || "").toLowerCase().includes(t));
    return (showAllBrands || t) ? base : base.slice(0, BRAND_COLLAPSED);
  }, [qBrand, marcas, showAllBrands]);

  const totalCats = useMemo(() => {
    const t = qCat.trim().toLowerCase();
    if (!t) return categorias.length;
    return categorias.filter((c) => (c.nombre || "").toLowerCase().includes(t)).length;
  }, [qCat, categorias]);

  const totalBrands = useMemo(() => {
    const t = qBrand.trim().toLowerCase();
    if (!t) return marcas.length;
    return marcas.filter((m) => (m.nombre || "").toLowerCase().includes(t)).length;
  }, [qBrand, marcas]);

  const pickCategory = (id) => {
    onCategory(id);
    if (window.innerWidth <= 768) setMobileOpen(false);
  };

  const pickBrand = (id) => {
    onBrand(id);
    if (window.innerWidth <= 768) setMobileOpen(false);
  };

  const showOverlay = mobileOpen && window.innerWidth <= 768;

  return (
    <div className="stf-scope">
      {showOverlay && (
        <div
          className="stf-overlay open"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={`stf-panel ${mobileOpen ? "mobile-open" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="stf-header">
          <div>
            <h3 className="stf-title">Filters</h3>
            <p className="stf-subtitle">Narrow down aircraft parts</p>
          </div>

          <div className="stf-header-actions">
            {hasActive && (
              <button type="button" className="stf-clear" onClick={onClear}>
                Clear
              </button>
            )}

            <button
              type="button"
              className="stf-close"
              onClick={() => setMobileOpen(false)}
              aria-label="Close filters"
            >
              ✕
            </button>
          </div>
        </div>

        {hasActive && (
          <div className="stf-active">
            {catId && (
              <button className="stf-chip" onClick={() => onCategory(null)}>
                Category: <b>{selectedCategoryName}</b>
                <span>×</span>
              </button>
            )}

            {brandId && (
              <button className="stf-chip" onClick={() => onBrand(null)}>
                Brand: <b>{selectedBrandName}</b>
                <span>×</span>
              </button>
            )}
          </div>
        )}

        {/* CATEGORY */}
        <section className="stf-block">
          <button className="stf-block-head" onClick={() => setOpenCat((v) => !v)}>
            <span>Category</span>
            <span className={`stf-chevron ${openCat ? "open" : ""}`}>▾</span>
          </button>

          {openCat && (
            <div className="stf-block-body">
              <input
                className="stf-input"
                placeholder="Search category..."
                value={qCat}
                onChange={(e) => {
                  setQCat(e.target.value);
                  setShowAllCats(false);
                }}
              />

              <div className="stf-list stf-list--scroll">
                {filteredCats.map((c) => {
                  const cid = Number(c.categoria_producto_id);
                  const active = catId === cid;

                  return (
                    <button
                      key={cid}
                      className={`stf-item ${active ? "active" : ""}`}
                      onClick={() => pickCategory(cid)}
                    >
                      <span className="stf-dot" />
                      <span className="stf-text">{c.nombre}</span>
                    </button>
                  );
                })}
              </div>

              {totalCats > CAT_COLLAPSED && !qCat.trim() && (
                <button
                  type="button"
                  className="stf-more"
                  onClick={() => setShowAllCats((v) => !v)}
                >
                  {showAllCats ? "Show less" : `Show all (${totalCats})`}
                </button>
              )}
            </div>
          )}
        </section>

        {/* BRAND */}
        <section className="stf-block">
          <button className="stf-block-head" onClick={() => setOpenBrand((v) => !v)}>
            <span>Brand</span>
            <span className={`stf-chevron ${openBrand ? "open" : ""}`}>▾</span>
          </button>

          {openBrand && (
            <div className="stf-block-body">
              <input
                className="stf-input"
                placeholder="Search brand..."
                value={qBrand}
                onChange={(e) => {
                  setQBrand(e.target.value);
                  setShowAllBrands(false);
                }}
              />

              <div className="stf-brand-grid stf-brand-grid--scroll">
                {filteredBrands.map((m) => {
                  const mid = Number(m.marca_producto_id);
                  const active = brandId === mid;
                  const src = m.url_image ? fileURL(m.url_image) : null;

                  return (
                    <button
                      key={mid}
                      className={`stf-brand ${active ? "active" : ""}`}
                      onClick={() => pickBrand(mid)}
                      title={m.nombre}
                    >
                      <div className="stf-brand-logo">
                        {src ? (
                          <img src={src} alt={m.nombre} loading="lazy" />
                        ) : (
                          <div className="stf-brand-placeholder">
                            {m.nombre?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                      </div>
                      <span className="stf-brand-name">{m.nombre}</span>
                    </button>
                  );
                })}
              </div>

              {totalBrands > BRAND_COLLAPSED && !qBrand.trim() && (
                <button
                  type="button"
                  className="stf-more"
                  onClick={() => setShowAllBrands((v) => !v)}
                >
                  {showAllBrands ? "Show less" : `Show all (${totalBrands})`}
                </button>
              )}
            </div>
          )}
        </section>
      </aside>
    </div>
  );
}

export default SmartFiltersPanel;
